const normalizeOptionKey = (value) => String(value ?? '').trim().toLowerCase();

const COLOR_SWATCH_MAP = {
  orange: '#f97316',
  green: '#22c55e',
  purple: '#a855f7',
  black: '#1a1a1a',
  white: '#ffffff',
  red: '#ef4444',
  blue: '#3b82f6',
  navy: '#1e3a5f',
  yellow: '#eab308',
  grey: '#9ca3af',
  gray: '#9ca3af',
  pink: '#ec4899',
  brown: '#92400e',
};

export const guessColorSwatch = (value) => {
  const normalized = String(value ?? '').toLowerCase().trim();
  if (COLOR_SWATCH_MAP[normalized]) return COLOR_SWATCH_MAP[normalized];
  for (const [name, hex] of Object.entries(COLOR_SWATCH_MAP)) {
    if (normalized.includes(name)) return hex;
  }
  return '#9ca3af';
};

const makeCellKey = (colorValue, sizeValue) =>
  `${normalizeOptionKey(colorValue)}::${normalizeOptionKey(sizeValue || '')}`;

const getVariantAttr = (variant, optionName) => {
  const attr = variant?.attributes?.find((a) => a.option_name === optionName);
  return String(attr?.option_value ?? '').trim();
};

export const canUseColorSizeMatrix = (variants = []) => {
  if (!variants.length) return true;
  return variants.every((variant) => {
    const names = (variant.attributes || []).map((a) => a.option_name);
    if (!names.length) return false;
    if (names.some((n) => !['color', 'size'].includes(n))) return false;
    const colorCount = names.filter((n) => n === 'color').length;
    const sizeCount = names.filter((n) => n === 'size').length;
    return colorCount <= 1 && sizeCount <= 1;
  });
};

export const variantsToColorSizeMatrix = (variants = [], basePrice = '') => {
  const colorOrder = [];
  const sizeOrder = [];
  const colorMedia = {};
  const cells = {};

  (variants || []).forEach((variant) => {
    const color = getVariantAttr(variant, 'color');
    const size = getVariantAttr(variant, 'size');
    if (color && !colorOrder.some((c) => normalizeOptionKey(c) === normalizeOptionKey(color))) {
      colorOrder.push(color);
    }
    if (size && !sizeOrder.some((s) => normalizeOptionKey(s) === normalizeOptionKey(size))) {
      sizeOrder.push(size);
    }

    const key = makeCellKey(color, size);
    cells[key] = {
      price: variant.price !== undefined && variant.price !== null ? String(variant.price) : String(basePrice || ''),
      isAvailable: variant.isAvailable !== false,
      sku: variant.sku || '',
    };

    if (color && (variant.media || []).length) {
      const cKey = normalizeOptionKey(color);
      if (!colorMedia[cKey] || (variant.media.length > (colorMedia[cKey]?.length || 0))) {
        colorMedia[cKey] = [...variant.media];
      }
    }
  });

  return {
    colors: colorOrder.map((value) => ({
      value,
      media: colorMedia[normalizeOptionKey(value)] || [],
    })),
    sizes: sizeOrder,
    cells,
  };
};

export const colorSizeMatrixToVariants = (matrix, basePrice = '', previousVariants = []) => {
  const { colors = [], sizes = [], cells = {} } = matrix || {};
  if (!colors.length) return [];

  const prevByKey = {};
  (previousVariants || []).forEach((v) => {
    const color = getVariantAttr(v, 'color');
    const size = getVariantAttr(v, 'size');
    prevByKey[makeCellKey(color, size)] = v;
  });

  const sizeList = sizes.length ? sizes : [''];
  const result = [];

  colors.forEach((colorRow) => {
    const color = String(colorRow.value || '').trim();
    if (!color) return;

    sizeList.forEach((sizeRaw) => {
      const size = String(sizeRaw || '').trim();
      const key = makeCellKey(color, size);
      const cell = cells[key] || {};
      const prev = prevByKey[key];
      const attributes = [{ option_name: 'color', option_value: color }];
      if (size) attributes.push({ option_name: 'size', option_value: size });

      const sku =
        cell.sku ||
        prev?.sku ||
        `${color}${size ? `-${size}` : ''}`.replace(/\s+/g, '-').toLowerCase();

      result.push({
        sku,
        price: cell.price !== undefined && cell.price !== '' ? cell.price : (prev?.price ?? basePrice),
        isAvailable: cell.isAvailable !== false,
        attributes,
        media: [...(colorRow.media || prev?.media || [])],
      });
    });
  });

  return result;
};

export const createEmptyColorSizeMatrix = () => ({
  colors: [],
  sizes: [],
  cells: {},
});

export const createEmptyVariant = (basePrice = '') => ({
  sku: '',
  price: basePrice,
  isAvailable: true,
  attributes: [{ option_name: 'color', option_value: '' }],
  media: [],
});

export const mapProductVariantsFromApi = (variants = []) =>
  (Array.isArray(variants) ? variants : []).map((variant) => ({
    sku: variant.sku || '',
    price: variant.price !== undefined && variant.price !== null ? String(variant.price) : '',
    isAvailable: variant.isAvailable !== false,
    attributes: (variant.attributes || []).length
      ? variant.attributes.map((attr) => ({
        option_name: attr.option_name || 'color',
        option_value: attr.option_value || '',
      }))
      : [{ option_name: 'color', option_value: '' }],
    media: (variant.images || []).map((img, index) => ({
      type: 'existing',
      mediaType: 'image',
      url: img.image || '',
      thumbnail: img.thumbnail || img.image || '',
      order: String(img.order ?? index + 1),
    })),
  }));

export const validateVariants = (variants = []) => {
  const seenCombos = new Set();
  for (let i = 0; i < variants.length; i += 1) {
    const variant = variants[i];
    const price = Number(variant.price);
    if (!variant.price || Number.isNaN(price) || price <= 0) {
      return `Please enter a valid price for Variant No. ${i + 1}`;
    }
    if (!variant.attributes?.length) {
      return `Please add at least one attribute to Variant No. ${i + 1}`;
    }
    for (const attr of variant.attributes) {
      if (!String(attr.option_value || '').trim()) {
        return `Please fill in all attribute values for Variant No. ${i + 1}`;
      }
    }
    const comboKey = (variant.attributes || [])
      .map((a) => `${a.option_name}:${normalizeOptionKey(a.option_value)}`)
      .sort()
      .join('|');
    if (seenCombos.has(comboKey)) {
      return `Duplicate variant combination at Variant No. ${i + 1}`;
    }
    seenCombos.add(comboKey);
  }
  return null;
};

export const mapProductImagesFromApi = (images = []) =>
  (Array.isArray(images) ? images : []).map((img, index) => ({
    type: 'existing',
    mediaType: 'image',
    url: img.image || '',
    thumbnail: img.thumbnail || img.image || '',
    order: String(img.order ?? index),
  }));

export const buildProductImagesSubmitPayload = (images = []) => {
  const imagesData = [];
  const productImageFiles = [];

  (images || []).forEach((item, index) => {
    if (item.type === 'new' && item.file) {
      productImageFiles.push(item.file);
      imagesData.push({
        mediaType: 'image',
        type: 'new',
        order: String(item.order ?? index),
      });
      return;
    }
    imagesData.push({
      mediaType: 'image',
      type: 'existing',
      url: item.url,
      thumbnail: item.thumbnail || item.url,
      order: String(item.order ?? index),
    });
  });

  return { imagesData, productImageFiles };
};

export const buildVariantsSubmitPayload = (variants = []) => {
  const variantsData = [];
  const variantImageFiles = [];

  variants.forEach((variant, vIdx) => {
    const mediaPayload = (variant.media || []).map((item, mIdx) => {
      if (item.type === 'new' && item.file) {
        variantImageFiles.push(item.file);
        return {
          mediaType: 'image',
          type: 'new',
          order: String(item.order || mIdx + 1),
        };
      }
      return {
        mediaType: 'image',
        type: 'existing',
        url: item.url,
        thumbnail: item.thumbnail || item.url,
        order: String(item.order || mIdx + 1),
      };
    });

    variantsData.push({
      sku: variant.sku || `${vIdx + 1}_${Date.now()}`,
      price: variant.price,
      isAvailable: variant.isAvailable !== false,
      attributes: variant.attributes || [],
      media: mediaPayload,
    });
  });

  return { variantsData, variantImageFiles };
};
